import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { Grid, Typography, Tabs, Tab } from '@mui/material';
import LCInfo from './LCInfo';
import LCMembers from './LCMembers';
import LCLeadership from './LCLeadership';
import LCResources from './LCResources';
import LCMore from './LCMore';
import { useApiGet } from '../../../utils/httpClientTs';
import { getLocalChapterDetail } from '../../../endpoints/chaptersTs';

type TabType = 'info' | 'leadership' | 'members' | 'resources' | 'more';

const LCDetail: React.FC = () => {
  let { id } = useParams<{ id: string }>();
  let [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key: string, value: string) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (!value || value === '') {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const { data } = useApiGet(
    ['lc-chapters-detail', id],
    () => getLocalChapterDetail(id!),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  // Create dynamic tab mapping based on available tabs (More hub links to Committees, Social, Events)
  const getAvailableTabs = useCallback((): TabType[] => {
    const tabs: TabType[] = ['info', 'leadership'];
    if (data?.local_chapter_access) {
      tabs.push('members', 'resources');
      if (data?.app_pilot_program) {
        tabs.push('more');
      }
    }
    return tabs;
  }, [data?.local_chapter_access, data?.app_pilot_program]);

  const convertTextToTabIndex = useCallback(
    (text: string): number => {
      const availableTabs = getAvailableTabs();
      const index = availableTabs.indexOf(text as TabType);
      // If the requested tab is not available, return 0 (info tab)
      return index >= 0 ? index : 0;
    },
    [getAvailableTabs]
  );

  const convertTabIndexToText = useCallback(
    (index: number): TabType => {
      const availableTabs = getAvailableTabs();
      return availableTabs[index] || 'info';
    },
    [getAvailableTabs]
  );

  const [tabSelected, setTabSelected] = useState<number>(0);
  const [initialTabProcessed, setInitialTabProcessed] =
    useState<boolean>(false);

  // Update tab selection when data loads or URL changes
  useEffect(() => {
    if (data) {
      const urlTab = searchParams.get('tab');
      if (urlTab) {
        const tabIndex = convertTextToTabIndex(urlTab);
        setTabSelected(tabIndex);
        setInitialTabProcessed(true);
      } else {
        setTabSelected(0);
        setInitialTabProcessed(true);
      }
    }
  }, [data, searchParams, convertTextToTabIndex, getAvailableTabs]);

  // Only update URL after we've processed the initial tab request
  useEffect(() => {
    if (initialTabProcessed) {
      const tabText = convertTabIndexToText(tabSelected);
      updateSearchParams('tab', tabText);
    }
  }, [
    tabSelected,
    updateSearchParams,
    convertTabIndexToText,
    initialTabProcessed,
  ]);

  return (
    <Grid container>
      <Grid item xs={12} sx={{ p: 2 }} textAlign={'center'}>
        {data?.is_disbanded ? (
          <Typography variant="h4" color="error">
            {data?.name} (Disbanded)
          </Typography>
        ) : (
          <Typography variant="h4">{data?.name}</Typography>
        )}
      </Grid>
      <Grid item xs={12} sx={{ p: 2 }}>
        <Tabs
          value={tabSelected}
          onChange={(event, newValue) => setTabSelected(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {getAvailableTabs().map((tab, index) => (
            <Tab
              key={tab}
              label={
                tab === 'info'
                  ? 'Info'
                  : tab === 'leadership'
                    ? 'Leadership'
                    : tab === 'members'
                      ? 'Members'
                      : tab === 'resources'
                        ? 'Resources'
                        : tab === 'more'
                          ? 'More'
                          : tab
              }
            />
          ))}
        </Tabs>
      </Grid>
      <Grid item xs={12} sx={{ p: 2 }}>
        {(() => {
          const currentTab = convertTabIndexToText(tabSelected);
          switch (currentTab) {
            case 'info':
              return (
                <LCInfo
                  id={id!}
                  lcAccess={data?.local_chapter_access}
                  lcAction={data?.local_chapter_action}
                  isAdmin={data?.is_admin || false}
                  isAdminOrSupport={data?.is_admin_or_support || false}
                />
              );
            case 'leadership':
              return (
                <LCLeadership
                  id={id!}
                  lcAccess={data?.local_chapter_access}
                  lcAction={data?.local_chapter_action}
                />
              );
            case 'members':
              return data?.local_chapter_access ? (
                <LCMembers
                  id={id!}
                  lcAccess={data?.local_chapter_access}
                  lcAction={data?.local_chapter_action}
                />
              ) : null;
            case 'more':
              return data?.local_chapter_access ? (
                <LCMore
                  id={id!}
                  localChapterAccess={data?.local_chapter_access}
                  appPilotProgram={data?.app_pilot_program}
                />
              ) : null;
            case 'resources':
              return data?.local_chapter_access ? (
                <LCResources
                  id={id!}
                  lcAccess={data?.local_chapter_access}
                  lcAction={data?.local_chapter_action}
                />
              ) : null;
            default:
              return <LCInfo id={id!} lcAccess={data?.local_chapter_access} />;
          }
        })()}
      </Grid>
    </Grid>
  );
};

export default LCDetail;
